import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { ArrowLeft, TrendingUp, TrendingDown, PieChart as PieIcon } from 'lucide-react';
import PeriodFilter, { applyPeriodFilter } from '../components/PeriodFilter';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, Legend } from 'recharts';

const COLORS_GREEN = ['#10b981', '#34d399', '#6ee7b7', '#a7f3d0', '#059669', '#047857', '#065f46', '#064e3b'];
const COLORS_RED = ['#ef4444', '#f87171', '#fca5a5', '#fecaca', '#dc2626', '#b91c1c', '#991b1b', '#7f1d1d'];

const formatCR = (n) => Math.round(parseFloat(n) || 0).toLocaleString('fr-FR');

/**
 * Page générique de détail des transactions de trésorerie.
 * type='enc'  : encaissements (credits)  | palette verte
 * type='dec'  : décaissements (debits payés) | palette rouge
 */
export default function DetailTresorerieFlow({ user, onLogout, type = 'enc', readOnly = false }) {
  const API = process.env.REACT_APP_BACKEND_URL;
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [period, setPeriod] = useState('all');

  const isEnc = type === 'enc';
  const palette = isEnc ? 'green' : 'red';
  const titleLabel = isEnc ? 'Encaissements' : 'Décaissements';
  const Icon = isEnc ? TrendingUp : TrendingDown;
  const sign = isEnc ? '+' : '−';
  const colors = isEnc ? COLORS_GREEN : COLORS_RED;
  const headerBg = isEnc ? 'from-green-600 to-green-700' : 'from-red-600 to-red-700';
  const accent = isEnc ? 'text-green-700' : 'text-red-700';

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API}/api/financial/transactions`);
        if (isEnc) {
          setRows(r.data?.credits || []);
        } else {
          // Décaissements = débits Payé
          const d = (r.data?.debits || []).filter(x => {
            const s = (x.statut || '').toLowerCase();
            return s === 'payé' || s === 'paye' || s === 'paid';
          });
          setRows(d);
        }
      } catch {
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [API, isEnc]);

  const filtered = useMemo(
    () => applyPeriodFilter(rows, period).slice().sort((a, b) => (b.date || '').localeCompare(a.date || '')),
    [rows, period]
  );

  // Aggrégation par catégorie pour le pie chart
  const pieData = useMemo(() => {
    const map = {};
    for (const r of filtered) {
      const cat = r.categorie || (isEnc ? r.annonceur : null) || 'Divers';
      const m = parseFloat(r.montant) || 0;
      map[cat] = (map[cat] || 0) + m;
    }
    return Object.entries(map)
      .map(([name, value]) => ({ name, value: Math.round(value) }))
      .sort((a, b) => b.value - a.value);
  }, [filtered, isEnc]);

  const total = pieData.reduce((s, x) => s + x.value, 0);

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid={`detail-${type}-page`}>
        {/* Header */}
        <div className="flex items-center gap-3 flex-wrap">
          <Button onClick={() => navigate(-1)} variant="outline" className="gap-2" data-testid="detail-back-btn">
            <ArrowLeft className="h-4 w-4" /> Retour
          </Button>
          <div className="flex-1">
            <h1 className={`text-3xl font-bold flex items-center gap-2 ${accent}`}>
              <Icon className="h-7 w-7" /> Détail des {titleLabel}
            </h1>
            <p className="text-gray-600 text-sm">
              {filtered.length} transaction{filtered.length > 1 ? 's' : ''} — Total : {sign}{formatCR(total)} €
            </p>
          </div>
          <PeriodFilter value={period} onChange={setPeriod} color={palette} testIdPrefix={`detail-${type}-period`} />
        </div>

        {/* Diagramme circulaire + Légende */}
        <Card className={`shadow-lg border-2 ${isEnc ? 'border-green-100' : 'border-red-100'}`}>
          <CardHeader className={`bg-gradient-to-r ${headerBg} text-white`}>
            <CardTitle className="flex items-center gap-2">
              <PieIcon className="h-5 w-5" /> Répartition par catégorie
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            {pieData.length === 0 ? (
              <p className="text-center text-gray-400 italic py-8">Aucune donnée pour la période sélectionnée</p>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-center">
                <div style={{ width: '100%', height: 320 }}>
                  <ResponsiveContainer>
                    <PieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        outerRadius={120}
                        innerRadius={50}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${(percent * 100).toFixed(0)}%`}
                      >
                        {pieData.map((entry, i) => (
                          <Cell key={i} fill={colors[i % colors.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(v) => `${formatCR(v)} €`} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div>
                  <table className="w-full text-sm">
                    <thead>
                      <tr className={`${isEnc ? 'bg-green-50 text-green-900' : 'bg-red-50 text-red-900'}`}>
                        <th className="px-3 py-2 text-left font-semibold">Catégorie</th>
                        <th className="px-3 py-2 text-right font-semibold">Montant</th>
                        <th className="px-3 py-2 text-right font-semibold">%</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {pieData.map((d, i) => {
                        const pct = total > 0 ? (d.value / total) * 100 : 0;
                        return (
                          <tr key={d.name} className="hover:bg-gray-50/50">
                            <td className="px-3 py-2 flex items-center gap-2">
                              <span className="inline-block w-3 h-3 rounded-sm" style={{ background: colors[i % colors.length] }}></span>
                              {d.name}
                            </td>
                            <td className={`px-3 py-2 text-right font-semibold ${accent}`}>{sign}{formatCR(d.value)}</td>
                            <td className="px-3 py-2 text-right text-gray-600">{pct.toFixed(1)}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className={`${isEnc ? 'bg-green-100 text-green-900' : 'bg-red-100 text-red-900'} font-bold`}>
                      <tr>
                        <td className="px-3 py-2">Total</td>
                        <td className="px-3 py-2 text-right">{sign}{formatCR(total)}</td>
                        <td className="px-3 py-2 text-right">100%</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tableau détaillé des transactions */}
        <Card className={`shadow-lg border-2 ${isEnc ? 'border-green-100' : 'border-red-100'}`}>
          <CardHeader className={`bg-gradient-to-r ${headerBg} text-white`}>
            <CardTitle>
              Liste des {titleLabel} ({filtered.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className={`${isEnc ? 'bg-green-100 text-green-900' : 'bg-red-100 text-red-900'}`}>
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Date</th>
                  <th className="px-3 py-2 text-left font-semibold">Catégorie</th>
                  <th className="px-3 py-2 text-left font-semibold">Description</th>
                  <th className="px-3 py-2 text-right font-semibold">Montant (€)</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan="4" className="text-center text-gray-400 py-6 text-sm italic">
                      Aucune transaction pour cette période
                    </td>
                  </tr>
                ) : (
                  filtered.map((r) => (
                    <tr key={r.id} className={`${isEnc ? 'hover:bg-green-50/40' : 'hover:bg-red-50/40'}`}>
                      <td className="px-3 py-2 whitespace-nowrap">{new Date(r.date).toLocaleDateString('fr-FR')}</td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${isEnc ? 'bg-green-500 text-white' : 'bg-red-500 text-white'}`}>
                          {r.categorie || (isEnc ? r.annonceur : null) || 'Divers'}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-gray-700 text-xs">{r.description || '—'}</td>
                      <td className={`px-3 py-2 text-right font-bold ${accent} whitespace-nowrap`}>
                        {sign}{(parseFloat(r.montant) || 0).toLocaleString('fr-FR')}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
              <tfoot className={`${isEnc ? 'bg-green-100 text-green-900' : 'bg-red-100 text-red-900'} font-bold`}>
                <tr>
                  <td colSpan="3" className="px-3 py-2 text-right">TOTAL :</td>
                  <td className="px-3 py-2 text-right">
                    {sign}{(filtered.reduce((s, r) => s + (parseFloat(r.montant) || 0), 0)).toLocaleString('fr-FR')} €
                  </td>
                </tr>
              </tfoot>
            </table>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
